import { cn } from '@/lib/utils';
import { type SearchFormData, type Site } from '@/types/search';
import { DesktopPill } from './desktop-pill';
import { MobileSheet } from './mobile-sheet';
import { SearchBarProvider, useSearchBar } from './use-search-bar';

export interface SearchBarProps {
  sites: Site[];
  initialData?: Partial<SearchFormData>;
  /**
   * How the search opens below `md`.
   * - `floating` (default): a fixed pill above the fold — for pages where search is secondary.
   * - `inline`: an in-flow field — for the home hero, where search *is* the page.
   */
  mobileTrigger?: 'floating' | 'inline';
  className?: string;
}

/**
 * The responsive split is CSS-only, and deliberately so.
 *
 * `useIsMobile()` returns false on the server and on the first client paint, then
 * flips after mount — so branching the tree on it hydrates in desktop mode and
 * remounts a frame later. Both surfaces render on every request; `md:` decides
 * which one is visible, and the server HTML matches the first client render.
 */
export function SearchBar({ sites, initialData, mobileTrigger = 'floating', className }: SearchBarProps) {
  const state = useSearchBar({ sites, initialData });

  return (
    <SearchBarProvider value={state}>
      <div className={cn('mx-auto w-full max-w-4xl', className)}>
        <DesktopPill className="hidden md:block" />
        <MobileSheet mobileTrigger={mobileTrigger} className="md:hidden" />
      </div>
    </SearchBarProvider>
  );
}
